<template>
  <Modal
    v-model="showPolygonDrawingModal"
    title="Draw Geospatial Polygon"
    fullscreen
    class="polygon-container"
  >
    <Form
      :label-width="100"
      @submit.native.prevent
    >
      <FormItem label="Center map at">
        <gmap-autocomplete
          style="width: 100%"
          @place_changed="updateCenter($event)"
        />
      </FormItem>
    </Form>
    <gmap-map
      ref="map"
      :center="center"
      :zoom="12"
      class="map-container"
    >
      <gmap-polygon
        v-if="paths.length > 0"
        ref="polygon"
        :paths="paths"
        :editable="true"
        :options="{
          strokeWeight: 0,
          fillOpacity: 0.45
        }"
        @paths_changed="updateEdited($event)"
      />
    </gmap-map>
    <div style="margin: 10px 0; direction: rtl;">
      <Button
        type="error"
        @click="removePolygon()"
      >
        Remove Polygon
      </Button>
    </div>
    <Form
      :label-width="100"
      @submit.native.prevent
    >
      <FormItem label="GeoJSON">
        <Input
          :value="JSON.stringify(polygonGeojson, null, 2)"
          :autosize="{ minRows: 5, maxRows: 10 }"
          type="textarea"
          @on-change="readGeojson"
        />
      </FormItem>
      <FormItem
        label="Sample Usage"
        class="query-container"
      >
        <codemirror
          :value="tripQuery"
          :options="cmOptions"
          class="query-editor"
          :style="{ height: `330px` }"
          @ready="onCmReady"
        />
      </FormItem>
      <FormItem
        v-if="errorMessage"
        label="Error"
      >
        {{ errorMessage }}
      </FormItem>
    </Form>
    <div style="margin: 10px 0; direction: rtl;">
      <Button
        type="success"
        @click="exportSampleQuery()"
      >
        Export Sample Query
      </Button>
    </div>
    <div slot="footer">
      <Button
        type="info"
        size="large"
        @click="onPolygonDrawn"
      >
        OK
      </Button>
      <Button
        type="default"
        size="large"
        @click="onModalClose"
      >
        Cancel
      </Button>
    </div>
  </Modal>
</template>

<script>
import _ from 'lodash'
import { gmapApi } from 'vue2-google-maps'
import GmapPolygon from 'vue2-google-maps/dist/components/polygon'

export default {

  components: {
    GmapPolygon
  },

  props: {
    isDrawingPolygon: Boolean,
    context: String
  },
  data () {
    return {
      showPolygonDrawingModal: false,
      drawingManager: null,
      center: { lat: 1.38, lng: 103.8 },
      edited: null,
      paths: [],
      mvcPaths: null,
      errorMessage: null,
      polygonGeojson: {
        type: 'LineString',
        coordinates: []
      },
      neLatitude: Number.NEGATIVE_INFINITY,
      swLatitude: Number.POSITIVE_INFINITY,
      neLongitude: Number.NEGATIVE_INFINITY,
      swLongitude: Number.POSITIVE_INFINITY,
      cmOptions: {
        theme: this.$root.preferences.queryPad.cmTheme || 'default',
        tabSize: 4,
        indentUnit: 4,
        autofocus: true,
        smartIndent: true,
        matchBrackets: true,
        closeBrackets: true,
        autoCloseBrackets: true,
        placeholder: `Only one query can run at a time... The query at the cursor's last position will run.`,
        mode: { name: 'text/x-sql', globalVars: true },
        lineNumbers: true,
        autoRefresh: true,
        indentWithTabs: true
      }
    }
  },

  computed: {
    google: gmapApi,

    tripQuery () {
      this.resetExtremeCoords()
      this.polygonGeojson.coordinates.forEach(coord => {
        if (coord[0] >= this.neLongitude) {
          this.neLongitude = coord[0]
        }
        if (coord[0] <= this.swLongitude) {
          this.swLongitude = coord[0]
        }
        if (coord[1] >= this.neLatitude) {
          this.neLatitude = coord[1]
        }
        if (coord[1] <= this.swLatitude) {
          this.swLatitude = coord[1]
        }
      })

      return `SELECT\n` +
        `  *\n` +
        `FROM\n` +
        `  LDW.COMMON.FACT_TRIP\n` +
        `WHERE\n` +
        `  START_LONGITUDE BETWEEN ${this.swLongitude} AND ${this.neLongitude}\n` +
        `  AND START_LATITUDE BETWEEN ${this.swLatitude} AND ${this.neLatitude}\n` +
        `  AND LDW.COMMON.POINT_IN_POLYGON(\n` +
        `    Array_construct(START_LATITUDE, START_LONGITUDE),\n` +
        `    '${this.fromGeoJSON(this.polygonGeojson)}'\n` +
        `  )\n` +
        `  AND ENDED_AT > '2019-05-01'\n` +
        `  AND ENDED_AT < '2019-05-10'\n` +
        `  AND TRIP_STATUS IN ('completed','terminated')`
    },

    queryConditions () {
      return `WHERE\n` +
          `  /* Please adjust your geo parameters and user defined function */\n` +
          `  START_LONGITUDE BETWEEN ${this.swLongitude} AND ${this.neLongitude}\n` +
          `  AND START_LATITUDE BETWEEN ${this.swLatitude} AND ${this.neLatitude}\n` +
          `  AND LDW.COMMON.POINT_IN_POLYGON(\n` +
          `    Array_construct(START_LATITUDE, START_LONGITUDE),\n` +
          `    '${this.fromGeoJSON(this.polygonGeojson)}'\n` +
          `  )`
    }
  },

  watch: {
    mvcPaths: {
      handler () {
        const path = []
        for (let i = 0; i < this.mvcPaths.getAt(0).getLength(); ++i) {
          let point = this.mvcPaths.getAt(0).getAt(i)
          path.push({ lat: point.lat(), lng: point.lng() })
        }
        this.paths = [ path ]

        this.polygonGeojson = {
          type: 'LineString',
          coordinates: this.paths[0].map(({ lat, lng }) => [lng, lat])
        }
      },
      deep: true
    },

    isDrawingPolygon (val) {
      this.showPolygonDrawingModal = val
    }
  },

  mounted () {
    this.$refs.map.$mapPromise.then((map) => {
      this.drawingManager = new this.google.maps.drawing.DrawingManager({
        drawingMode: this.google.maps.drawing.OverlayType.POLYGON,
        drawingControl: true,
        drawingControlOptions: {
          position: this.google.maps.ControlPosition.TOP_CENTER,
          drawingModes: ['polygon']
        },
        polygonOptions: {
          strokeWeight: 0,
          fillOpacity: 0.45,
          editable: true
        }
      })
      this.drawingManager.setMap(map)

      this.google.maps.event.addListener(this.drawingManager, 'overlaycomplete', event => {
        this.paths.push(event.overlay.getPath())
        const convertedPath = event.overlay.getPath().getArray().map(pair => [pair.lng(), pair.lat()])
        this.polygonGeojson = {
          type: 'LineString',
          coordinates: convertedPath
        }

        // Switch back to non-drawing mode after drawing a shape.
        this.drawingManager.setDrawingMode(null)
        this.drawingManager.setOptions({
          drawingControl: false
        })

        event.overlay.setMap(null)
      })
    })
  },

  methods: {
    /**
     * Update map center based on location
     * @param {google.maps.places.PlaceResult Object} place - info about a place
     */
    updateCenter (place) {
      if (_.has(place, ['geometry', 'location'])) {
        this.center = {
          lat: place.geometry.location.lat(),
          lng: place.geometry.location.lng()
        }
      } else {
        console.error(`Place is not a valid PlaceResult Object: ${JSON.stringify(place)}`)
      }
    },

    /**
     * Event handler for polygon change
     * @param {google.maps.MVCArray} mvcPaths
     */
    updateEdited (mvcPaths) {
      this.mvcPaths = mvcPaths
    },

    /**
     * Remove the polygon
     */
    removePolygon () {
      this.paths = []
      this.polygonGeojson = {
        type: 'LineString',
        coordinates: []
      }

      this.drawingManager.setDrawingMode(this.google.maps.drawing.OverlayType.POLYGON)
      this.drawingManager.setOptions({
        drawingControl: true
      })
    },

    /**
     * Read geo json from user input
     * @param {DOMEvent} $event - input event
     */
    readGeojson ($event) {
      try {
        this.polygonGeojson = JSON.parse($event.target.value)
        this.paths = this.polygonGeojson.coordinates.map(([lng, lat]) => ({ lat, lng }))
        this.errorMessage = null
        this.drawingManager.setDrawingMode(null)
        this.drawingManager.setOptions({
          drawingControl: false
        })
      } catch (err) {
        this.errorMessage = err.message
        this.drawingManager.setDrawingMode(this.google.maps.drawing.OverlayType.POLYGON)
        this.drawingManager.setOptions({
          drawingControl: true
        })
      }
    },

    /**
     * Encodes a GeoJSON LineString feature/geometry.
     * @param {Object} originalGeojson
     * @param {Number} precision
     * @return {String}
     */
    fromGeoJSON (originalGeojson, precision) {
      const geojson = originalGeojson && originalGeojson.type === 'Feature' ? originalGeojson.geometry : originalGeojson
      if (!geojson || geojson.type !== 'LineString') {
        this.errorMessage = 'Input must be a GeoJSON LineString'
        return
      }
      return this.encodeGeoJSON(this.flipCoords(geojson.coordinates), precision)
    },

    /**
     * Decodes to a GeoJSON LineString geometry.
     * @param {String} str
     * @param {Number} precision
     * @return {Object}
     */
    toGeoJSON (str, precision) {
      const coords = this.decodePolylineStr(str, precision)
      return {
        type: 'LineString',
        coordinates: this.flipCoords(coords)
      }
    },

    /**
     * Export sample geospatial query to Query Pad
     */
    exportSampleQuery () {
      this.$emit('exportSampleGeospatialQuery', this.tripQuery)
      this.onModalClose()
    },

    /**
     * Sets properties for codemirror component once they are ready (created).
     * @param {Object} cm
     */
    onCmReady (cm) {
      const doc = cm.getDoc()
      doc.clearHistory()
    },

    /**
     * Handles OK button
     */
    onPolygonDrawn () {
      if (this.paths.length) {
        this.$emit('patchGeospatialConditions', this.queryConditions)
      }
      this.onModalClose()
    },

    /**
     * Handles Cancel button
     */
    onModalClose () {
      this.removePolygon()
      this.showPolygonDrawingModal = false
      this.$emit('isDrawingPolygon', this.showPolygonDrawingModal)
    },

    /**
     * Encodes the given [latitude, longitude] coordinates array.
     * @param {Array.<Array.<Number>>} coordinates
     * @param {Number} precision
     * @return {String}
     */
    encodeGeoJSON (coordinates, precision = 5) {
      if (!coordinates.length) {
        return ''
      }

      const factor = Math.pow(10, precision)
      let output = this.encode(coordinates[0][0], 0, factor) + this.encode(coordinates[0][1], 0, factor)

      for (let i = 1; i < coordinates.length; i++) {
        const a = coordinates[i]
        const b = coordinates[i - 1]
        output += this.encode(a[0], b[0], factor)
        output += this.encode(a[1], b[1], factor)
      }

      return output
    },

    /**
     * Decodes to a [latitude, longitude] coordinates array.
     * This is adapted from the implementation in Project-OSRM.
     * @param {String} str
     * @param {Number} precision
     * @return {Array}
     *
     * @see https://github.com/Project-OSRM/osrm-frontend-legacy/blob/master/WebContent/routing/OSRM.RoutingGeometry.js
     */
    decodePolylineStr (str, precision = 5) {
      const coords = []
      const factor = Math.pow(10, precision)

      let index = 0
      let lat = 0
      let lng = 0
      let shift = 0
      let result = 0
      let byte = null

      let latitudeChange
      let longitudeChange

      while (index < str.length) {
        // Reset shift, result, and byte
        byte = null
        shift = 0
        result = 0
        do {
          byte = str.charCodeAt(index++) - 63
          result |= (byte & 0x1f) << shift
          shift += 5
        } while (byte >= 0x20)
        latitudeChange = ((result & 1) ? ~(result >> 1) : (result >> 1))
        shift = result = 0
        do {
          byte = str.charCodeAt(index++) - 63
          result |= (byte & 0x1f) << shift
          shift += 5
        } while (byte >= 0x20)
        longitudeChange = ((result & 1) ? ~(result >> 1) : (result >> 1))
        lat += latitudeChange
        lng += longitudeChange
        coords.push([lat / factor, lng / factor])
      }

      return coords
    },

    // Util functions
    /**
     * Util function to encode lat & long value into string
     * Detail about the encoding algorithm can be found at https://developers.google.com/maps/documentation/utilities/polylinealgorithm
     * @param {Number} current
     * @param {Number} previous
     * @param {Number} factor
     */
    encode (current, previous, factor) {
      current = Math.round(current * factor)
      previous = Math.round(previous * factor)
      let coordinate = current - previous
      coordinate <<= 1
      if (current - previous < 0) {
        coordinate = ~coordinate
      }
      let output = ''
      while (coordinate >= 0x20) {
        output += String.fromCharCode((0x20 | (coordinate & 0x1f)) + 63)
        coordinate >>= 5
      }
      output += String.fromCharCode(coordinate + 63)
      return output
    },

    /**
     * Flip coordinate array
     * @param {Array} coords coordinates array
     * @return {Array} flipped lat lon position coordinates array
     */
    flipCoords (coords) {
      const flipped = []
      for (var i = 0; i < coords.length; i++) {
        flipped.push(coords[i].slice().reverse())
      }
      return flipped
    },

    /**
     * Reset all extreme coordinates
     */
    resetExtremeCoords () {
      this.neLatitude = Number.NEGATIVE_INFINITY
      this.swLatitude = Number.POSITIVE_INFINITY
      this.neLongitude = Number.NEGATIVE_INFINITY
      this.swLongitude = Number.POSITIVE_INFINITY
    }
  }
}
</script>

<style scoped>
  .map-container {
    width: 100%;
    height: 600px;
  }

  .query-container >>> .CodeMirror pre.CodeMirror-placeholder {
    color: var(--color-mid-gray);
  }

  .query-container >>> .CodeMirror {
    height: 100% !important;
  }
</style>
